/**
 * ConnectedMarketShare -- wires URL params and Apollo queries to the library's
 * pure MarketShareRenderer component.
 *
 * This follows the "connected wrapper" pattern established by ConnectedTreemap.
 * During migration, existing chart code in src/visualization/charts/marketshare
 * continues to work unchanged. New pages or apps can use this wrapper (or
 * compose their own) to adopt the library components.
 *
 * Data flow:
 *   URL params (usePageQueryParams)
 *     -> Apollo query (useQuery)
 *       -> Chart component (renders SVG lines)
 *         -> MarketShareRenderer (layout, legend, vizState)
 *
 * Tooltip isolation is provided by TooltipStateIsolationProvider; hover
 * updates do not rerender this module (query, legend, category state).
 */

import { memo, useMemo, useRef } from "react";
import { useQuery } from "@apollo/client/react";
import { useMarketshareParams } from "../paramSchemas";
import { getCategoriesForChartLegend } from "@atlas-int/viz";
import { determineEndpointFacet, mapQueryArgumentsToAPIEndpointInputs } from "@atlas-int/data";
import { worldGroupDatum, ColorBy, LegendLabels, useYearDefaults } from "@atlas-int/data";
import { useHiddenCategories } from "@atlas-int/viz/treemap";
import {
  useVisualizationState,
  LegendRow,
  GenericResizeContainer,
  TooltipStateIsolationProvider,
} from "@atlas-int/viz/shared";
import { CategoryLabels } from "@atlas-int/viz/shared";
import type { CategoryDatum } from "@atlas-int/viz/shared";
import MarketShareWithTooltip from "./MarketShareWithTooltip";
import VisualizationLayout from "../components/VisualizationLayout";

const onHighlightNoop = () => {};

const ConnectedMarketShare: React.FC = () => {
  const [
    {
      exporter,
      view,
      productClass,
      productLevel,
      startYear: yearMin,
      endYear: yearMax,
      servicesClass,
      tradeDirection,
    },
    setQuery,
  ] = useMarketshareParams();

  // Clamp start/end year URL params to the available range for the current product class
  useYearDefaults({
    startYear: yearMin,
    endYear: yearMax,
    productClass,
    setQuery,
    needsYearRange: true,
  });

  const importer = useMemo(() => worldGroupDatum.groupId, []);

  const marketshareElement = useRef<HTMLDivElement | null>(null);

  const { hiddenCategories, setHiddenCategories } = useHiddenCategories(
    view,
    ColorBy.Sector,
  );

  const narrowedInputVariables = useMemo(
    () => mapQueryArgumentsToAPIEndpointInputs({ exporter, importer, view }),
    [exporter, importer, view],
  );

  const marketShareInputVariables = useMemo(
    () => ({
      productClass,
      yearMin,
      yearMax,
      productLevel,
      productId: undefined,
      servicesClass,
      ...narrowedInputVariables,
    }),
    [
      productClass,
      yearMin,
      yearMax,
      productLevel,
      servicesClass,
      narrowedInputVariables,
    ],
  );

  const { queryToUse, queryIsInvalid } = useMemo(
    () =>
      determineEndpointFacet({
        view,
        variables: marketShareInputVariables,
      }),
    [view, marketShareInputVariables],
  );

  const { loading, error, data, previousData } = useQuery<{
    data: Record<string, unknown>[];
  }>(queryToUse, {
    variables: marketShareInputVariables,
    skip: queryIsInvalid,
  });

  const vizState = useVisualizationState({
    loading,
    error,
    data,
    previousData,
  });

  const successResponse = data || previousData;

  const categoriesForLegend: CategoryDatum[] = useMemo(
    () =>
      getCategoriesForChartLegend({
        view,
        productClass,
      }),
    [view, productClass],
  );

  return (
    <VisualizationLayout
      totalValue={undefined}
      findInVizOptions={[]}
      highlightedItem={undefined}
      onHighlight={onHighlightNoop}
    >
      <GenericResizeContainer chartContainerRef={marketshareElement}>
        <TooltipStateIsolationProvider dataVersion={successResponse}>
          <MarketShareWithTooltip
            chartContainerRef={marketshareElement}
            successResponse={successResponse}
            exporter={exporter}
            yearMin={yearMin}
            yearMax={yearMax}
            productClass={productClass}
            servicesClass={servicesClass}
            hiddenCategories={hiddenCategories}
            currentTradeDirection={tradeDirection}
            view={view}
            loading={loading}
            hasError={vizState.hasError}
            errorNoticeType={vizState.errorNoticeType}
            hasNoData={vizState.hasNoData}
          />
        </TooltipStateIsolationProvider>
        <LegendRow>
          <CategoryLabels
            legendLabel={LegendLabels.Sector}
            categories={categoriesForLegend}
            allowToggle={true}
            hiddenCategories={hiddenCategories}
            setHiddenCategories={setHiddenCategories}
            resetText="Show All Sectors"
            fullWidth={true}
          />
        </LegendRow>
      </GenericResizeContainer>
    </VisualizationLayout>
  );
};

export default memo(ConnectedMarketShare);
